import { Link, useParams } from "react-router-dom";

import { linkTo } from "../api/links";
import { useAcks, useBatch, useChanges } from "../api/queries";
import type { Ack, Batch, Change } from "../api/types";
import { ColumnsPanel } from "../components/Columns";
import { LoadMore } from "../components/MasterDetail";
import {
  Badge,
  Empty,
  ErrorBox,
  EventType,
  Field,
  FieldGrid,
  Guid,
  Json,
  Num,
  OutcomeBadge,
  OutcomeSummary,
  Panel,
  ProvenanceBadge,
  ReasonBadge,
  SEQ,
  Spinner,
  StatusBadge,
  Time,
} from "../components/primitives";

/**
 * The batch as the engine stored it: the events verbatim, and what the apply did with each.
 * An event with no journal row either dedupated or never parsed — both are visible states,
 * because "nothing happened" is the answer support most often needs.
 */
export function BatchDetail() {
  const { batchGuid = "" } = useParams();
  const query = useBatch(batchGuid);
  const journal = useChanges({ batch_guid: batchGuid, limit: 200 });

  if (query.isPending) return <Spinner />;
  if (query.error) return <ErrorBox error={query.error} />;

  return <BatchView batch={query.data} journal={journal} />;
}

function BatchView({ batch, journal }: { batch: Batch; journal: ReturnType<typeof useChanges> }) {
  const acks = useAcks({
    device_guid: batch.device_guid,
    root_guid: batch.root_guid,
    carrier_guid: batch.carrier_guid,
  });
  const changes = journal.data?.pages.flatMap((page) => page.changes) ?? [];
  const ack = acks.data?.pages[0]?.acks[0];

  const events = batch.events.map((raw, index) => {
    const guid = eventGuid(raw);
    return { index, raw, guid, journal: changes.filter((row) => guid && row.event_guid === guid) };
  });
  const outcomes: Partial<Record<string, number>> = {};
  for (const row of changes) outcomes[row.outcome] = (outcomes[row.outcome] ?? 0) + 1;
  const known = new Set(events.flatMap((event) => (event.guid ? [event.guid] : [])));
  const unmatched = changes.filter((row) => !row.event_guid || !known.has(row.event_guid));

  return (
    <div className="space-y-4 p-5">
      {batch.status === "pending" && <PendingNote journaled={changes.length > 0} />}
      <Panel
        title="Batch"
        id="batch"
        actions={
          <StatusBadge status={batch.status} counts={journal.hasNextPage ? undefined : outcomes} />
        }
      >
        <FieldGrid>
          <Field label="batch">
            <Guid value={batch.batch_guid} />
          </Field>
          <Field label={batch.root_type}>
            <Guid value={batch.root_guid} to={linkTo.root(batch.root_type, batch.root_guid)} />
          </Field>
          <Field label="driver">
            <Guid value={batch.driver_guid} to={linkTo.driver(batch.driver_guid)} />
          </Field>
          <Field label="device">
            <Guid value={batch.device_guid} to={linkTo.device(batch.device_guid)} />
          </Field>
          <Field label="carrier">
            <Guid value={batch.carrier_guid} to={linkTo.carrier(batch.carrier_guid)} />
          </Field>
          <Field label="connection at receipt">
            {batch.connection_status ? (
              <Badge tone={batch.connection_status === "activated" ? "good" : "warn"}>
                {batch.connection_status}
              </Badge>
            ) : (
              <Badge tone="bad">none</Badge>
            )}
          </Field>
          <Field label="provenance">
            <ProvenanceBadge provenance={batch.provenance} />
          </Field>
          <Field label="received">
            <Time value={batch.received_at} relative={false} />
          </Field>
          <Field label="applied">
            {batch.applied_at ? (
              <Time value={batch.applied_at} relative={false} />
            ) : (
              <Badge tone="warn">not yet</Badge>
            )}
          </Field>
          <Field label="attempts">{batch.attempts}</Field>
          <Field label="events">{batch.events.length}</Field>
          <Field label="journal">
            <OutcomeSummary
              counts={outcomes}
              loading={journal.isPending}
              error={journal.error}
              pending={batch.status === "pending"}
              partial={journal.hasNextPage}
            />
          </Field>
          <Field label="device ack for this load">
            <AckValue ack={ack} loading={acks.isPending} />
          </Field>
        </FieldGrid>

        {batch.last_error && (
          <div className="border-t border-line px-4 py-3">
            <div className="label mb-1">last error</div>
            <pre className="mono overflow-auto rounded border border-bad/30 bg-bad-bg p-2.5 text-bad">
              {batch.last_error}
            </pre>
          </div>
        )}
      </Panel>

      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <Panel title={`Events (${events.length})`} id="batch-events">
          {journal.error && <ErrorBox error={journal.error} />}
          {events.length === 0 && <Empty>An empty batch. It still records an ack.</Empty>}
          {events.map((event) => (
            <EventBlock
              key={`${event.index}-${event.guid ?? "no-guid"}`}
              index={event.index}
              raw={event.raw}
              journal={event.journal}
              loading={journal.isPending}
            />
          ))}

          {unmatched.length > 0 && (
            <div className="border-t border-line p-4">
              <div className="label mb-2">
                journal rows this batch produced without a matching event guid
              </div>
              <div className="space-y-2">
                {unmatched.map((entry) => (
                  <JournalLine key={entry.seq} entry={entry} />
                ))}
              </div>
            </div>
          )}
          <LoadMore
            hasNext={journal.hasNextPage}
            loading={journal.isFetchingNextPage}
            onClick={() => void journal.fetchNextPage()}
          />
        </Panel>

        <Panel title="Client metadata" id="batch-metadata" className="self-start">
          <div className="p-3">
            <Json value={batch.metadata} />
          </div>
        </Panel>
      </div>

      <ColumnsPanel
        table="inbox_batches"
        id="batch-columns"
        row={batch}
        omit={["events", "metadata"]}
        self="batch_guid"
      />
    </div>
  );
}

function AckValue({ ack, loading }: { ack: Ack | undefined; loading: boolean }) {
  if (loading) return <span className="text-xs text-faint">loading…</span>;
  if (!ack) return <span className="text-xs text-faint">none</span>;
  return (
    <span className="mono text-xs" title={SEQ.appliedTo}>
      cursor {ack.applied_seq} ·{" "}
      <Guid value={ack.last_applied_batch_guid} to={linkTo.batch(ack.last_applied_batch_guid)} />
    </span>
  );
}

/**
 * `pending` is both the normal state for a fraction of a second and the terminal state of a
 * lost trigger, and it does not mean nothing happened: each event commits in its own
 * transaction, so a batch can be half applied and still say pending.
 */
function PendingNote({ journaled }: { journaled: boolean }) {
  return (
    <div className="rounded-md border border-warn/40 bg-warn-bg p-4 text-sm text-warn">
      <p className="font-semibold">Stored, outcome not yet decided.</p>
      <p className="mt-1 text-body">
        {journaled
          ? "Partly applied: some of these events are already in the journal and in the load's " +
            "state. A re-drain runs only the tail — the committed prefix noops on the event-guid " +
            "dedupe."
          : "No event of this batch has reached the journal yet."}
      </p>
      <p className="mt-2 text-xs text-muted">
        The device already has its receipt; what is waiting is the outcome — the load's state, the
        feed cursor and the ack. Draining is per load, oldest batch first, under one lock: this row
        applies when its load's next drain runs, and if the apply trigger was lost at receipt, that
        is the next batch for the same load. Nothing else re-fires it.
      </p>
    </div>
  );
}

function text(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/**
 * The event guid as the journal serializes it. Devices may send it uppercase, braced or without
 * hyphens, and older batches under `eventGuid` — the engine accepts all of them.
 */
function eventGuid(raw: Record<string, unknown>): string | null {
  const value = text(raw["event_guid"]) ?? text(raw["eventGuid"]);
  if (!value) return null;
  const hex = value.replace(/[{}-]/g, "").toLowerCase();
  if (!/^[0-9a-f]{32}$/.test(hex)) return value.toLowerCase();
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function EventBlock({
  index,
  raw,
  journal,
  loading,
}: {
  index: number;
  raw: Record<string, unknown>;
  journal: Change[];
  loading: boolean;
}) {
  const type = text(raw["type"]);
  const version = raw["schema_version"] ?? raw["schemaVersion"];
  const occurredAt = text(raw["occurred_at"]) ?? text(raw["occurredAt"]);
  const guid = eventGuid(raw);

  return (
    <div className="row">
      <div className="flex flex-wrap items-center gap-3">
        <span className="mono text-faint">#{index}</span>
        <span className="text-sm font-medium text-strong">{type ?? "unparsed envelope"}</span>
        {typeof version === "number" && <Badge tone="muted">v{version}</Badge>}
        {occurredAt && (
          <span
            className="mono text-[11px] text-muted"
            title="occurred_at — when the device says the driver did it. The journal stores only server write time, so this is the only place the two can be compared."
          >
            on device <Time value={occurredAt} />
          </span>
        )}
        {!loading && journal.length === 0 && <Badge tone="muted">no journal row</Badge>}
        {journal.map((entry) => (
          <span key={entry.seq} className="flex items-center gap-1.5">
            <OutcomeBadge outcome={entry.outcome} />
            <ReasonBadge code={entry.reason_code} outcome={entry.outcome} />
          </span>
        ))}
        <span className="ml-auto flex items-center gap-1 text-xs">
          <span className="label">event</span>
          <Guid value={guid} to={linkTo.event(guid)} />
        </span>
      </div>

      {!loading && journal.length === 0 && (
        <p className="mt-1.5 text-xs text-muted">
          Either a duplicate the journal already had (the dedupe returns before writing), or an
          envelope the parser could not read — the row for it is listed below the events.
        </p>
      )}

      {journal.map((entry) => (
        <div key={entry.seq} className="mt-2">
          <JournalLine entry={entry} />
        </div>
      ))}

      <div className="mt-2">
        <Json value={raw} collapsed />
      </div>
    </div>
  );
}

function JournalLine({ entry }: { entry: Change }) {
  const timeline = linkTo.rootTimeline(entry.root_type, entry.root_guid);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded border border-line bg-canvas px-2.5 py-1.5 text-xs">
      <Num label="id" value={entry.seq} hint={SEQ.id} />
      <Num label="cursor" value={entry.conn_seq} hint={SEQ.cursor} />
      <OutcomeBadge outcome={entry.outcome} />
      <ReasonBadge code={entry.reason_code} outcome={entry.outcome} />
      <EventType value={entry.event_type} />
      <span className="text-muted">
        {entry.entity_type} · {entry.operation} · v{entry.version}
      </span>
      {entry.winning_event_guid && (
        <span className="flex items-center gap-1">
          <span className="label">won by</span>
          <Guid value={entry.winning_event_guid} to={linkTo.event(entry.winning_event_guid)} />
        </span>
      )}
      {timeline && (
        <Link to={timeline} className="ml-auto text-link hover:underline">
          in timeline →
        </Link>
      )}
    </div>
  );
}
